import React, { useEffect, useState, useCallback, } from "react";
import Pagination from "@/components/Pagination";
import SearchInput from "@/components/SearchBar";
import { useUpdateUser, useUsers, useDeletedUsers } from "@/hooks/useTenantDashboardService";
import { API_BASE_URL_IMAGE } from "@/config/api.enpoints";
import { useUserStore } from "@/store";
import Edit from "@/assets/editicon.png";
import useAuthStore from "@/store/useAuthStore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { resendPasswordSetupEmail, forceDeleteUser, softDeleteUser, restoreUser } from "@/api/userService";
import DeleteUserConfirmationDialog from "@/components/Popup/DeleteUserConfirmationDialog";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { canManageUsers } from '@/config/permissions';

/**
 * Simple debounce hook — tiny, battle-tested pattern.
 * Keeps the input responsive while only updating the "debounced" value after `delay` ms of inactivity.
 */
function useDebounce<T>(value: T, delay = 350) {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

interface UserType {
  id: string;
  name: string;
  email: string;
  role: string;
  userImg?: string;
  isVerified: boolean;
  status?: "active" | "inactive";
  companyName?: string;
  phoneNumber?: string;
}

const Users: React.FC = () => {
  const { isDark } = useDashboardTheme();
  // UI state
  const [activeButton, setActiveButton] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 350); // <-- prevents hitting API on each keystroke
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string>("newest"); // newly added

  // Delete + menu state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isPermanentDelete, setIsPermanentDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openKebabMenu = Boolean(anchorEl);

  // stores / hooks
  const { isAddingUser, setIsAddingUser, editUserData, setPageHeader } = useUserStore();
  const { mutate: updateUser } = useUpdateUser();
  const navigate = useNavigate();
  const { role } = useAuthStore();

  const showArchived = canManageUsers(role as string);
  const showRoleFilters = canManageUsers(role as string);
  const roleButtons = [
    { id: 1, label: "All Roles" },
    ...(showRoleFilters ? [
      { id: 2, label: "Company Admin" },
      { id: 3, label: "Manager" },
    ] : []),
    { id: 4, label: "Sales Rep" },
    ...(showArchived ? [{ id: 5, label: "Archived" }] : []),
  ];

  // map and selection
  const roleMap: Record<number, string> = {
    1: "",
    2: "ADMIN",
    3: "MANAGER",
    4: "USER",
  };

  const selectedRole =
    activeButton === 1 && role === "SUPER_ADMIN" ? undefined : roleMap[activeButton];

  const getSortParams = () => {
    switch(sortBy) {
      case "newest": return { orderBy: "createdAt", order: "desc" as const };
      case "oldest": return { orderBy: "createdAt", order: "asc" as const };
      case "updatedDesc": return { orderBy: "updatedAt", order: "desc" as const };
      case "updatedAsc": return { orderBy: "updatedAt", order: "asc" as const };
      default: return { orderBy: "createdAt", order: "desc" as const };
    }
  };
  const { orderBy, order } = getSortParams();

  // IMPORTANT: use the debounced search term for the API hook so we don't re-run on every keystroke.
  const { data: userData, refetch } = useUsers({
    currentPage,
    pageSize: 10,
    search: debouncedSearchTerm,
    role: selectedRole,
    currentAuthRole: activeButton === 5 ? "" : (role ?? ""),
    orderBy,
    order,
  });

  const { data: deletedUserData, refetch: refetchDeleted } = useDeletedUsers({
    currentPage,
    pageSize: 10,
    search: debouncedSearchTerm,
    enabled: activeButton === 5,
    orderBy,
    order,
  });

  const isArchivedTab = activeButton === 5;
  const allUsers = isArchivedTab ? (deletedUserData?.data?.items ?? []) : (userData?.data?.items ?? []);
  const totalPages = isArchivedTab ? (deletedUserData?.data?.totalPages ?? 1) : (userData?.data?.totalPages ?? 1);

  const headingText = editUserData ? "Edit User" : isAddingUser ? "Create Users" : "User Management";

  useEffect(() => {
    setPageHeader(headingText);
  }, [headingText, setPageHeader]);

  // Reset page when debounced search, role, or active tab changes (prevents API thrash when user types).
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedRole, activeButton]);

  // if editUserData appears, ensure add-user panel shows
  useEffect(() => {
    if (editUserData && !isAddingUser) {
      setIsAddingUser(true);
    }
  }, [editUserData, isAddingUser, setIsAddingUser]);

  // handlers (memoized where beneficial)
  const handleRoleButton = useCallback((id: number) => setActiveButton(id), []);
  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  // Menu open/close tied to the specific user context
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, u: UserType) => {
    setAnchorEl(event.currentTarget);
    setUserToDelete(u);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    // keep userToDelete until the menu closes; if you want to clear it immediately uncomment next line
    // setUserToDelete(null);
  }, []);

  // delete flow
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      if (isPermanentDelete) {
        await forceDeleteUser(userToDelete.id);
        toast.success(`User ${userToDelete.name} permanently deleted.`);
      } else {
        await softDeleteUser(userToDelete.id);
        toast.success(`User ${userToDelete.name} archived successfully.`);
      }
      if (isArchivedTab) {
        refetchDeleted();
      } else {
        refetch();
      }
    } catch (error) {
      toast.error(isPermanentDelete ? "Failed to permanently delete user." : "Failed to archive user.");
      console.error("Delete user error:", error);
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      setAnchorEl(null);
    }
  };

  // toggle status (optimized)
  const handleToggleStatus = useCallback(
    (u: UserType) => {
      if (!u.isVerified) return;
      const newStatus = u.status === "active" ? "inactive" : "active";
      updateUser(
        { id: u.id, status: newStatus },
        {
          onSuccess: () => {
            refetch();
          },
          onError: () => {
            toast.error("Failed to update status");
          },
        },
      );
    },
    [updateUser, refetch],
  );

  // render
  return (
    <div className={isDark ? "" : "light-theme-dashboard"} style={{ background: isDark ? "" : "#F4F5F7", minHeight: "100vh" }}>
      <div className="flex justify-between items-center px-6 pt-6">
        {!isAddingUser && (
          <SearchInput placeholder="Search here..." value={searchTerm} onChange={handleSearch} />
        )}
        {canManageUsers(role as string) && role !== "SUPER_ADMIN" && (
          <button
            className="px-4 py-2 bg-[#B8F890] font-semibold poppins text-black rounded-full cursor-pointer"
            onClick={() => {
              navigate("/user/new");
            }}
          >
            Add New User
          </button>
        )}
      </div>

      <main className="flex-1 p-2">
        <div className="min-h-[75vh] rounded-xl p-6 mx-auto flex flex-col gap-6 shadow-lg credit-usage-container">
          <>
            <div className="flex space-x-4 mb-2">
              <select
                className="bg-transparent text-white border border-[#313131] rounded-md px-4 py-2 poppins focus:outline-none focus:ring-1 focus:ring-[#B8F890]"
                value={activeButton}
                onChange={(e) => handleRoleButton(Number(e.target.value))}
              >
                {roleButtons.map((btn) => (
                  <option key={btn.id} value={btn.id} className="bg-[#1C1A26]">
                    {btn.label}
                  </option>
                ))}
              </select>

              <select
                className="bg-transparent text-white border border-[#313131] rounded-md px-4 py-2 poppins focus:outline-none focus:ring-1 focus:ring-[#B8F890]"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest" className="bg-[#1C1A26]">Newest First</option>
                <option value="oldest" className="bg-[#1C1A26]">Oldest First</option>
                <option value="updatedDesc" className="bg-[#1C1A26]">Recently Updated</option>
                <option value="updatedAsc" className="bg-[#1C1A26]">Least Recently Updated</option>
              </select>
            </div>
            {isArchivedTab && (
              <div className="bg-[#2B231B] border-l-4 border-amber-500 p-4 rounded-md text-amber-200 text-sm poppins flex items-center gap-3">
                <span className="text-amber-500 font-bold text-lg">⚠️</span>
                <div>
                  <p className="font-semibold">Archived Users Status Details:</p>
                  <p>Archived users are soft-deleted. Once a user is archived, they cannot perform any task or use any feature, and will not be able to log in to the platform at all.</p>
                </div>
              </div>
            )}

            <div className="h-full bg-[#1C1A26] rounded-lg overflow-hidden credit-usage-card">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#1A1824] text-gray-300 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="py-3 px-6">Name</th>
                    <th className="py-3 px-6">Email</th>
                    <th className="py-3 px-6 text-center">Verified</th>
                    <th className="py-3 px-6">Status</th>
                    <th className="py-3 px-6">Role</th>
                    <th className="py-3 px-6 text-center">
                      {role === "MANAGER" ? "Views" : "Actions"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.length > 0 ? (
                    allUsers.map((u: UserType, idx: number) => (
                      <tr
                        key={u.id ?? idx}
                        className="border-t border-[#313131] text-white hover:bg-[#222] transition"
                      >
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-4">
                            {u.userImg ? (
                              <img
                                src={
                                  u.userImg.startsWith("http://") || u.userImg.startsWith("https://")
                                    ? u.userImg
                                    : API_BASE_URL_IMAGE + u.userImg
                                }
                                alt={u.name}
                                className="w-10 h-10 rounded-full object-cover border border-gray-600"
                              />
                            ) : (
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm"
                                style={{
                                  backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 15%, transparent)",
                                  color: "var(--color-stat-accent)",
                                }}
                              >
                                {u.name
                                  ?.split(" ")
                                  .map((p) => p[0])
                                  .join("")
                                  .toUpperCase()
                                  .slice(0, 2)}
                              </div>
                            )}
                            <span className="font-medium text-sm">{u.name}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-300">{u.email}</td>
                        <td className="py-4 px-6 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div
                              className="w-11 h-6 rounded-full relative transition duration-200 p-0.5"
                              style={{
                                backgroundColor: u.isVerified ? "var(--color-stat-accent)" : "#9CA3AF",
                              }}
                            >
                              <div
                                className={`w-5 h-5 bg-white rounded-full shadow-md transform transition duration-200 ${
                                  u.isVerified ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </div>
                            <span
                              className={`mt-1 text-xs font-medium ${
                                u.isVerified ? "text-green-400" : "text-gray-400"
                              }`}
                            />
                          </div>
                        </td>

                        <td className="py-4 px-6 text-center">
                          <div
                            onClick={() => !isArchivedTab && handleToggleStatus(u)}
                            className={`w-11 h-6 rounded-full relative transition duration-200 p-0.5 ${
                              isArchivedTab ? "" : "cursor-pointer"
                            } ${(!u.isVerified || isArchivedTab) ? "opacity-50 cursor-not-allowed" : ""}`}
                            title={isArchivedTab ? "Archived user" : !u.isVerified ? "User is unverified" : "Click to toggle status"}
                            style={{
                              backgroundColor: u.status === "active" ? "var(--color-stat-accent)" : "#9CA3AF",
                            }}
                          >
                            <div
                              className={`w-5 h-5 bg-white rounded-full shadow-md transform transition duration-200 ${
                                u.status === "active" ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </div>
                          <span
                            className={`mt-1 text-xs font-medium ${
                              u.status === "active" ? "text-blue-400" : "text-gray-400"
                            }`}
                          />
                        </td>

                        <td className="py-4 px-6">
                          <span className="text-sm font-semibold text-green-400 capitalize">
                            {u.role === "USER"
                              ? u.companyName
                                ? "Company User"
                                : "Individual User"
                              : u.role === "ADMIN"
                              ? "Company Admin"
                              : u.role}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          {role === "SUPER_ADMIN" || role === "ADMIN" ? (
                            <>
                              <IconButton
                                aria-label="more"
                                id={`long-button-${u.id}`}
                                aria-controls={openKebabMenu ? `long-menu-${u.id}` : undefined}
                                aria-expanded={openKebabMenu ? "true" : undefined}
                                aria-haspopup="true"
                                onClick={(e) => handleMenuOpen(e, u)}
                                sx={{ color: "white" }}
                              >
                                <MoreVertIcon />
                              </IconButton>
 
                              <Menu
                                id={`long-menu-${u.id}`}
                                MenuListProps={{
                                  "aria-labelledby": `long-button-${u.id}`,
                                }}
                                anchorEl={anchorEl}
                                open={openKebabMenu && userToDelete?.id === u.id}
                                onClose={handleMenuClose}
                                PaperProps={{
                                  style: {
                                    backgroundColor: "#1C1A26",
                                    color: "white",
                                    border: "1px solid #313131",
                                  },
                                }}
                              >
                                {isArchivedTab ? (
                                  <>
                                    <MenuItem
                                      onClick={async () => {
                                        handleMenuClose();
                                        try {
                                          await restoreUser(u.id);
                                          toast.success(`User ${u.name} restored successfully.`);
                                          refetchDeleted();
                                        } catch (error) {
                                          toast.error("Failed to restore user.");
                                          console.error("Restore user error:", error);
                                        }
                                      }}
                                      sx={{ "&:hover": { backgroundColor: "#2e2e2e" } }}
                                    >
                                      Restore User
                                    </MenuItem>
 
                                    <MenuItem
                                      onClick={() => {
                                        handleMenuClose();
                                        setIsPermanentDelete(true);
                                        setShowDeleteConfirm(true);
                                      }}
                                      sx={{
                                        color: "red",
                                        "&:hover": { backgroundColor: "#2e2e2e" },
                                      }}
                                    >
                                      Delete User
                                    </MenuItem>
                                  </>
                                ) : (
                                  <>
                                    <MenuItem
                                      onClick={() => {
                                        handleMenuClose();
                                        navigate(`/user/${u.id}`);
                                      }}
                                      sx={{ "&:hover": { backgroundColor: "#2e2e2e" } }}
                                    >
                                      Edit User
                                    </MenuItem>
 
                                    <MenuItem
                                      onClick={async () => {
                                        handleMenuClose();
                                        try {
                                          await resendPasswordSetupEmail(u.id);
                                          toast.success("Password setup email sent!");
                                        } catch (error) {
                                          toast.error("Failed to send password setup email.");
                                          console.error("Resend password error:", error);
                                        }
                                      }}
                                      sx={{ "&:hover": { backgroundColor: "#2e2e2e" } }}
                                    >
                                      Resend Password Link
                                    </MenuItem>
 
                                    <MenuItem
                                      onClick={() => {
                                        handleMenuClose();
                                        setIsPermanentDelete(false);
                                        setShowDeleteConfirm(true);
                                      }}
                                      sx={{
                                        color: "orange",
                                        "&:hover": { backgroundColor: "#2e2e2e" },
                                      }}
                                    >
                                      Archive User
                                    </MenuItem>
 
                                    <MenuItem
                                      onClick={() => {
                                        handleMenuClose();
                                        setIsPermanentDelete(true);
                                        setShowDeleteConfirm(true);
                                      }}
                                      sx={{
                                        color: "red",
                                        "&:hover": { backgroundColor: "#2e2e2e" },
                                      }}
                                    >
                                      Delete User
                                    </MenuItem>
                                  </>
                                )}
                              </Menu>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                navigate(`/user/${u.id}`);
                              }}
                              className="hover:scale-105 transition-transform"
                              title={role === "MANAGER" ? "View" : "Edit"}
                            >
                              {role === "MANAGER" ? (
                                <VisibilityIcon sx={{ fontSize: 20, color: "#90CAF9", cursor: "pointer" }} />
                              ) : (
                                <img src={Edit} alt="Edit" className="w-4 h-4 inline-block" />
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-gray-400">
                        No Record Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
          {!isAddingUser && totalPages > 1 && (
            <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          )}
        </div>
      </main>

      {userToDelete && (
        <DeleteUserConfirmationDialog
          open={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
          userName={userToDelete.name}
          isPermanent={isPermanentDelete}
        />
      )}
    </div>
  );
};

export default Users;
